// The nightly numbers, as steps. Used by the numbers page (rail, progress) and the End of day hub.
import { sectionOn, type EodCalc, type EodConfig, type EodData } from './eod';

export type EodStepId = 'printouts' | 'sales' | 'cash' | 'payments' | 'extras' | 'labour' | 'service' | 'submit';
export interface EodStepState { id: EodStepId; label: string; done: boolean; problem: boolean; summary: string | null }

const near0 = (x: number | null | undefined) => x != null && Math.abs(x) < 0.01;
const m = (n: number | null | undefined) =>
  n == null ? '—' : `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const EOD_STEP_LABELS: Record<EodStepId, string> = {
  printouts: 'Printouts',
  sales: 'Sales',
  cash: 'Cash count',
  payments: 'Payments',
  extras: 'Discounts and extras',
  labour: 'Labour',
  service: 'Service summary',
  submit: 'Notes and submit',
};
export const EOD_STEP_ORDER: EodStepId[] = ['printouts', 'sales', 'cash', 'payments', 'extras', 'labour', 'service', 'submit'];

/** The steps this venue uses, from its report setup. */
export function eodStepOrder(cfg?: EodConfig): EodStepId[] {
  if (!cfg) return EOD_STEP_ORDER.filter((s) => s !== 'service');
  const on = (id: Parameters<typeof sectionOn>[1]) => sectionOn(cfg, id);
  const extras = on('discounts') || on('tips') || on('debtors') || on('offers') || on('surcharges');
  return EOD_STEP_ORDER.filter((s) =>
    s === 'cash' ? on('cash') : s === 'payments' ? on('payments') : s === 'extras' ? extras : s === 'labour' ? on('labour') : s === 'service' ? on('serviceSummary') : true);
}

export function eodSteps(d: EodData, k: EodCalc, opts: { submitted?: boolean; visited?: Set<EodStepId>; cfg?: EodConfig } = {}): EodStepState[] {
  const order = eodStepOrder(opts.cfg);
  const visited = opts.visited ?? new Set<EodStepId>();
  const xreads = Object.values(d.xreads ?? {}).filter(Boolean).length;
  const photos = (d.close ? 1 : 0) + xreads;

  const cashExplained = near0(k.cashVariance) || (d.cash.note ?? '').trim().length > 3;
  const payExplained = near0(k.paymentsVariance) || (d.payments.note ?? '').trim().length > 3;
  const discountsOk = (d.discounts ?? []).every((x) => !x.amount || x.detail.trim().length > 2);
  const deletedOk = !(d.deleted?.amount) || d.deleted.detail.trim().length > 2;
  const extrasHasData = (d.discounts ?? []).length > 0 || !!d.deleted?.amount || !!d.refunds?.amount || !!d.tips?.cash || (d.debtors ?? []).some((x) => x.amount);

  const cashDone = k.cashTakings != null || !!d.cash.noCash;
  const payDone = k.paymentsVariance != null && (k.cashTakings != null || !!d.cash.noCash);

  const serviceFilled = Object.values(d.service ?? {}).some((x) => Object.values(x.stations ?? {}).some((v) => v.trim()) || Object.values(x.notes ?? {}).some((v) => v.trim()));
  const all: EodStepState[] = [
    { id: 'printouts', label: EOD_STEP_LABELS.printouts, done: !!d.close, problem: false,
      summary: photos ? `${photos} photo${photos === 1 ? '' : 's'} read` : null },
    { id: 'sales', label: EOD_STEP_LABELS.sales, done: k.total != null && k.covers != null, problem: false,
      summary: k.total != null ? `${m(k.total)}${k.covers != null ? ` · ${k.covers} covers` : ''}` : null },
    { id: 'cash', label: EOD_STEP_LABELS.cash, done: cashDone && cashExplained, problem: cashDone && !cashExplained,
      summary: d.cash.noCash ? 'No cash taken' : k.cashVariance == null ? null : near0(k.cashVariance) ? 'Balanced' : `${m(Math.abs(k.cashVariance))} ${k.cashVariance < 0 ? 'short' : 'over'}` },
    { id: 'payments', label: EOD_STEP_LABELS.payments, done: payDone && payExplained, problem: payDone && !payExplained,
      summary: k.paymentsVariance == null ? null : near0(k.paymentsVariance) ? 'Matches sales' : `${m(k.paymentsVariance)} off` },
    { id: 'extras', label: EOD_STEP_LABELS.extras, done: discountsOk && deletedOk && (visited.has('extras') || extrasHasData), problem: !discountsOk || !deletedOk,
      summary: k.discountsTotal ? `${m(k.discountsTotal)} discounts` : extrasHasData ? 'Added' : null },
    { id: 'labour', label: EOD_STEP_LABELS.labour, done: k.labourTotal != null, problem: false,
      summary: k.labourTotal != null ? `${m(k.labourTotal)}${k.labourPct != null ? ` · ${k.labourPct}%` : ''}` : null },
    { id: 'service', label: EOD_STEP_LABELS.service, done: serviceFilled, problem: false,
      summary: serviceFilled ? 'Filled in' : null },
    { id: 'submit', label: EOD_STEP_LABELS.submit, done: !!opts.submitted, problem: false,
      summary: opts.submitted ? 'Submitted' : null },
  ];
  return order.map((id) => all.find((x) => x.id === id)!);
}

/** First step that still needs something. */
export function nextStep(steps: EodStepState[]): EodStepState {
  return steps.find((s) => !s.done && s.id !== 'submit') ?? steps[steps.length - 1];
}
